import { AppLink } from '@/shared/components/AppLink';
import { cn } from '@/shared/utils/cn';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsDesktop, useIsMobile } from '@/core/hooks/useMediaQuery';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { useMultivariateExperiment } from '@/shared/features/multivariateexperiment/presentation/hooks/useMultivariateExperiment';
import SectionTitle from './SectionTitle';
import {
  HOME_PAGE_CATEGORY_CARD_CLICKED,
  homePageCategoryCardClickEventProperties,
} from '../analytics/homePageCategoryCard';
import {
  HOME_PAGE_BANNER_CLICKED,
  HOME_PAGE_BANNER_VIEWED,
  NEW_HOME_BANNER_TYPE_COLLECTION,
  NEW_HOME_HANDPICKED_BANNER_SECTION_NAME,
  newHomeCollectionBannerBaseEventProperties,
  resolveNewHomeOfferBannerDisplayName,
} from '../analytics/homePageBannerCarousel';
import {
  BROWSE_ALL_COLLECTIONS_CLICKED,
  BROWSE_ALL_COLLECTIONS_FROM_SOURCE_HOME_PAGE,
  browseAllCollectionsClickedEventProperties,
} from '@/shared/features/analytics/utils/browseAllCollectionsClicked';
import {
  resolveItemImageUrl,
  resolveItemLink,
  resolveItemSliderImageUrls,
  resolveItemTileTitle,
  type GridPresentationVm,
  type SectionItemPresentationVm,
} from '../viewModels/newHome.vm';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/shared/widgets/carousel';
import Autoplay from 'embla-carousel-autoplay';

interface GridSectionProps {
  data: GridPresentationVm;
  sectionKey: string;
  eventLabel: string;
}

/** Passed to `IntersectionObserver` (`useIntersectionObserver` `threshold`). Tune freely. */
const HANDPICKED_BANNER_VIEWED_IO_THRESHOLDS: number[] = [0, 0.25, 0.5, 0.75, 1];

/**
 * Tile must be at least this visible (0–1) to fire `Home Page Banner Viewed`.
 * Below 1 so horizontally scrollable / peeking second-row tiles still qualify when meaningfully in view.
 */
const HANDPICKED_BANNER_VIEWED_MIN_INTERSECTION_RATIO = 0.5;

function HandpickedBannerViewportGate({
  enabled,
  item,
  isDesktop,
  viewedPositionsRef,
  children,
}: {
  enabled: boolean;
  item: SectionItemPresentationVm;
  isDesktop: boolean;
  viewedPositionsRef: React.MutableRefObject<Set<number>>;
  children: React.ReactNode;
}) {
  const { analytics, isInitialized } = useAnalytics();
  const position = item.position;

  const analyticsRef = useRef(analytics);
  const isInitRef = useRef(isInitialized);
  const itemRef = useRef(item);
  const isDesktopRef = useRef(isDesktop);

  useLayoutEffect(() => {
    analyticsRef.current = analytics;
    isInitRef.current = isInitialized;
    itemRef.current = item;
    isDesktopRef.current = isDesktop;
  }, [analytics, isInitialized, item, isDesktop]);

  const observerEnabled = enabled && isInitialized && Boolean(analytics);

  const onIntersect = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (!entry.isIntersecting || entry.intersectionRatio < HANDPICKED_BANNER_VIEWED_MIN_INTERSECTION_RATIO) {
        return;
      }
      if (viewedPositionsRef.current.has(position)) return;
      const a = analyticsRef.current;
      if (!a || !isInitRef.current) return;
      viewedPositionsRef.current.add(position);
      const bannerDisplayName = resolveNewHomeOfferBannerDisplayName(itemRef.current, isDesktopRef.current);
      try {
        a.trackEvent(
          HOME_PAGE_BANNER_VIEWED,
          newHomeCollectionBannerBaseEventProperties({
            bannerDisplayName,
            bannerPosition: position,
          }),
        );
      } catch (err) {
        console.error('Error tracking Home Page Banner Viewed (handpicked viewport):', err);
      }
    },
    [position, viewedPositionsRef],
  );

  const { ref } = useIntersectionObserver<HTMLDivElement>(
    { threshold: HANDPICKED_BANNER_VIEWED_IO_THRESHOLDS, enabled: observerEnabled },
    onIntersect,
  );

  if (!enabled) return <>{children}</>;

  return (
    <div ref={ref} className="min-h-0 min-w-0 h-full w-full">
      {children}
    </div>
  );
}

function GridItemSlider({
  imageUrls,
  orientation,
  autoPlay,
  interval,
  title,
  blankImageUrl,
  lazyload,
  imageAlt,
  isHandpicked = false,
}: {
  imageUrls: string[];
  orientation: 'horizontal' | 'vertical';
  autoPlay: boolean;
  interval: number;
  title: string;
  blankImageUrl: string;
  lazyload?: string;
  /** When set (e.g. ""), overrides alt text for SEO (duplicate link + heading pattern). */
  imageAlt?: string;
  isHandpicked?: boolean;
}) {
  const plugins = autoPlay
    ? [
        Autoplay({
          delay: interval,
          stopOnInteraction: true,
        }),
      ]
    : [];

  return (
    <Carousel
      opts={{ align: 'start', loop: true }}
      orientation={orientation}
      plugins={plugins}
      className="h-full"
    >
      <CarouselContent
        viewportClassName="h-full"
        className={cn(
          orientation === 'horizontal' ? '-ml-0 h-full' : '-mt-0 flex-col h-full'
        )}
      >
        {imageUrls.map((url, idx) => (
          <CarouselItem
            key={`${url}-${idx}`}
            className={cn('basis-full h-full', orientation === 'horizontal' ? 'pl-0' : 'pt-0')}
          >
            <img
              src={url}
              alt={imageAlt !== undefined ? imageAlt : title}
              className={cn("w-full h-full", !isHandpicked && "object-cover")}
              loading={lazyload ? 'lazy' : 'eager'}
              onError={(e) => {
                if (blankImageUrl) {
                  (e.target as HTMLImageElement).src = blankImageUrl;
                }
              }}
            />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
}

function resolveSliderOrientation({
  sliderOrientation,
  aspectRatio,
}: {
  sliderOrientation?: 'horizontal' | 'vertical';
  aspectRatio: string;
}): 'horizontal' | 'vertical' {
  if (sliderOrientation === 'horizontal' || sliderOrientation === 'vertical') return sliderOrientation;

  // Missing: infer from section-level aspect ratio (SSR-safe).
  // - 3:4 is portrait/tall → vertical slider
  // - everything else defaults to horizontal (including 1:1, 4:3, and bento/auto)
  if (aspectRatio === '3:4') return 'vertical';
  return 'horizontal';
}

/** Invisible full-bleed hit target: navigates like the tile image without adding a second `<a>`. */
function CategoryMediaHitButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="absolute inset-0 z-[1] cursor-pointer border-0 bg-transparent p-0"
      aria-hidden
      tabIndex={-1}
      onClick={onClick}
    />
  );
}

/**
 * Shop by Category: single canonical link on the heading (`<h2><a>`), decorative images, optional non-anchor hit layer on static media.
 * Extracted from `GridItem` to keep the default grid path readable.
 */
function CategorySeoGridItem({
  item,
  outerStyle,
  tileShellClass,
  isCircle,
  tileImageSrc,
  sliderUrls,
  displayTitle,
  itemLink,
  showVideoResolved,
  showSliderResolved,
  showBottomOverlay,
  showTitle,
  isOverlay,
  categoryDesktopMasonry,
  aspectRatio,
  blankImageUrl,
  hasShadow,
}: {
  item: SectionItemPresentationVm;
  outerStyle?: CSSProperties;
  tileShellClass: string;
  isCircle: boolean;
  tileImageSrc: string;
  sliderUrls: string[];
  displayTitle: string;
  itemLink: string;
  showVideoResolved: boolean;
  showSliderResolved: boolean;
  showBottomOverlay: boolean;
  showTitle: boolean;
  isOverlay: boolean;
  categoryDesktopMasonry: boolean;
  aspectRatio: string;
  blankImageUrl: string;
  hasShadow: boolean;
}) {
  const { analytics, isInitialized } = useAnalytics();
  const navigate = useNavigate();

  const categoryCardLinkAnalytics = {
    eventName: HOME_PAGE_CATEGORY_CARD_CLICKED,
    eventProperties: homePageCategoryCardClickEventProperties({
      categoryName: displayTitle,
      position: item.position,
    }),
  };

  const overlayTitleLinkClass = cn(
    'pointer-events-auto inline-block w-full px-3 pb-3 pt-6 font-semibold whitespace-pre-line text-base leading-none tracking-[0.01em] capitalize no-underline outline-offset-2 font-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 md:text-2xl md:pb-4 md:pt-10',
    categoryDesktopMasonry ? 'md:text-left md:pl-4 md:font-serif md:text-xl md:font-normal' : 'text-center',
  );

  const showHitLayer = !showSliderResolved && !item.bgColor && Boolean(itemLink);
  const onMediaNavigate = useCallback(() => {
    if (analytics && isInitialized) {
      try {
        analytics.trackEvent(
          HOME_PAGE_CATEGORY_CARD_CLICKED,
          homePageCategoryCardClickEventProperties({
            categoryName: displayTitle,
            position: item.position,
          }),
        );
      } catch (err) {
        console.error(`Error tracking event "${HOME_PAGE_CATEGORY_CARD_CLICKED}":`, err);
      }
    }
    if (item.shouldRefresh || /goldcoin|build-your-own-diamond|\/tnc\.html/i.test(itemLink)) {
      window.location.assign(itemLink);
      return;
    }
    navigate(itemLink);
  }, [analytics, isInitialized, item.position, displayTitle, itemLink, item.shouldRefresh, navigate]);

  return (
    <div className={cn('block', isCircle && 'flex flex-col items-center')} style={outerStyle}>
      <div className={tileShellClass}>
        <div className={cn('w-full h-full relative', hasShadow && 'overflow-hidden rounded-[inherit]')}>
          {item.bgColor ? (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center px-3 text-center"
              style={{ backgroundColor: item.bgColor }}
            >
              <h2 className="m-0 text-sm font-semibold leading-tight text-white md:text-base">
                <AppLink
                  to={itemLink}
                  shouldRefresh={item.shouldRefresh}
                  {...categoryCardLinkAnalytics}
                  className="text-inherit no-underline hover:underline"
                >
                  {displayTitle}
                </AppLink>
              </h2>
              {item.collection ? (
                <p className="mt-1 text-xs leading-snug text-white/90 md:text-sm">{item.collection}</p>
              ) : null}
            </div>
          ) : showVideoResolved ? (
            <>
              <video
                className="w-full h-full object-cover"
                playsInline
                muted
                loop
                autoPlay
                preload="metadata"
                poster={tileImageSrc}
              >
                <source src={item.videoUrl} type="video/mp4" />
              </video>
              {showHitLayer ? <CategoryMediaHitButton onClick={onMediaNavigate} /> : null}
            </>
          ) : showSliderResolved ? (
            <GridItemSlider
              imageUrls={sliderUrls}
              orientation={resolveSliderOrientation({
                sliderOrientation: item.sliderOrientation,
                aspectRatio,
              })}
              autoPlay={item.sliderAutoPlay ?? true}
              interval={item.sliderInterval ?? 2500}
              title={displayTitle}
              blankImageUrl={blankImageUrl}
              lazyload={item.lazyload}
              imageAlt=""
            />
          ) : (
            <>
              <img
                src={tileImageSrc}
                alt=""
                className="w-full h-full object-fill"
                loading={item.lazyload ? 'lazy' : 'eager'}
                onError={(e) => {
                  if (blankImageUrl) {
                    (e.target as HTMLImageElement).src = blankImageUrl;
                  }
                }}
              />
              {showHitLayer ? <CategoryMediaHitButton onClick={onMediaNavigate} /> : null}
            </>
          )}

          {showBottomOverlay && (
            <div
              className={cn(
                'pointer-events-none absolute bottom-0 left-0 right-0 z-[2] rounded-b-[5px]',
                categoryDesktopMasonry && 'md:rounded-b-2xl',
              )}
              style={{
                background:
                  'linear-gradient(to top, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.18) 60%, transparent 100%)',
              }}
            >
              <h2 className="m-0">
                <AppLink
                  to={itemLink}
                  shouldRefresh={item.shouldRefresh}
                  {...categoryCardLinkAnalytics}
                  className={overlayTitleLinkClass}
                  style={{ color: item.overlayTextColor ?? '#ffffff' }}
                >
                  {displayTitle}
                </AppLink>
              </h2>
            </div>
          )}
        </div>
      </div>

      {showTitle && !isOverlay && (
        <h2 className={cn('m-0 mt-1.5 text-center text-[11px] leading-tight', isCircle && 'px-0.5')}>
          <AppLink
            to={itemLink}
            shouldRefresh={item.shouldRefresh}
            {...categoryCardLinkAnalytics}
            className="text-inherit no-underline hover:underline"
          >
            {displayTitle}
          </AppLink>
        </h2>
      )}
    </div>
  );
}

function GridItem({
  item,
  sectionKey,
  shape,
  aspectRatio,
  showTitle,
  titlePosition,
  blankImageUrl,
  categoryDesktopMasonry,
  useGridArea = true,
  analyticsSectionName: _analyticsSectionName,
}: {
  item: SectionItemPresentationVm;
  sectionKey: string;
  shape: string;
  aspectRatio: string;
  showTitle: boolean;
  titlePosition: string;
  blankImageUrl: string;
  /** @deprecated no longer used — overlay-bottom title now shows on both mobile and desktop */
  overlayBottomDesktopOnly?: boolean;
  /** Desktop-only Shop by Category: larger radius, bottom-left serif labels */
  categoryDesktopMasonry: boolean;
  useGridArea?: boolean;
  /** Matches `data-analytics-section-name` on the parent section (Shop by Category, etc.). */
  analyticsSectionName?: string;
}) {
  const isDesktop = useIsDesktop();
  const isCircle = shape === 'circle';
  const isOverlay = titlePosition === 'overlay-bottom';
  const isBento = aspectRatio === 'auto';
  const showBottomOverlay = showTitle && isOverlay && !item.bgColor;

  const tileImageSrc = resolveItemImageUrl(item, isDesktop);
  const sliderUrls = resolveItemSliderImageUrls(item, isDesktop);
  const displayTitle = resolveItemTileTitle(item, isDesktop);
  const itemLink = resolveItemLink(item, isDesktop);
  const showVideoResolved =
    Boolean(item.showVideo && item.videoUrl) && !(item.videoMobileOnly && isDesktop);
  const showSliderResolved =
    Boolean(item.showSlider && sliderUrls.length > 0) &&
    (!isDesktop || item.showSliderOnDesktop !== false);

  const itemAnalyticsProps = {
    eventName: `${sectionKey} Item Clicked` as const,
    eventProperties: { position: item.position, title: displayTitle },
  };

  const handpickedBannerName =
    displayTitle.trim() || item.categoryName?.trim() || item.collection?.trim() || '';

  const isHandpickedBrowseAllCollectionsTile =
    sectionKey === 'handpicked' && item.position === 4;

  const handpickedBrowseAllCollectionsAnalyticsProps = isHandpickedBrowseAllCollectionsTile
    ? {
        eventName: BROWSE_ALL_COLLECTIONS_CLICKED,
        eventProperties: browseAllCollectionsClickedEventProperties({
          fromSource: BROWSE_ALL_COLLECTIONS_FROM_SOURCE_HOME_PAGE,
          category: item.label?.trim() || handpickedBannerName || 'View All Collections',
        }),
      }
    : null;

  const linkAnalyticsProps =
    sectionKey === 'handpicked'
      ? handpickedBrowseAllCollectionsAnalyticsProps ?? {
          eventName: HOME_PAGE_BANNER_CLICKED,
          eventProperties: {
            'Banner Name': handpickedBannerName,
            'Banner Position': item.position,
            'Section Name': NEW_HOME_HANDPICKED_BANNER_SECTION_NAME,
            'Banner Type': NEW_HOME_BANNER_TYPE_COLLECTION,
          },
        }
      : itemAnalyticsProps;

  const { homePageVariant: homeVariant } = useMultivariateExperiment();
  const isCategoryCard = sectionKey === 'categoryGrid';
  const hasShadow = isCategoryCard && (homeVariant === 'V2' || homeVariant === 'V3' || homeVariant === 'V4' || homeVariant === 'V5' || homeVariant === 'V6' || homeVariant === 'V7' || homeVariant === 'V8');

  const outerStyle = useGridArea ? { gridArea: item.gridArea } : undefined;
  const tileShellClass = cn(
    'relative',
    !hasShadow && 'overflow-hidden',
    isCircle ? 'aspect-square rounded-full' : 'rounded-[5px]',
    !isCircle && categoryDesktopMasonry && 'md:rounded-2xl',
    !isCircle && !isBento && aspectRatio === '1:1' && 'aspect-square',
    !isCircle && !isBento && aspectRatio === '3:4' && 'aspect-[3/4]',
    !isCircle && !isBento && aspectRatio === '4:3' && 'aspect-[4/3]',
    isBento && 'h-full',
    hasShadow && "transition-all duration-300 ease-[cubic-bezier(0.25,0.8,0.25,1)] [transform:translate3d(0,0,0)] shadow-[0_4px_10px_#000000c9] md:shadow-[0_6px_16px_#000000c9] md:hover:-translate-y-[2px] md:hover:scale-[1.015] md:hover:shadow-[0_20px_38px_#000000c9] hover:z-10"
  );

  if (sectionKey === 'categoryGrid') {
    return (
      <CategorySeoGridItem
        item={item}
        outerStyle={outerStyle}
        tileShellClass={tileShellClass}
        isCircle={isCircle}
        tileImageSrc={tileImageSrc}
        sliderUrls={sliderUrls}
        displayTitle={displayTitle}
        itemLink={itemLink}
        showVideoResolved={showVideoResolved}
        showSliderResolved={showSliderResolved}
        showBottomOverlay={showBottomOverlay}
        showTitle={showTitle}
        isOverlay={isOverlay}
        categoryDesktopMasonry={categoryDesktopMasonry}
        aspectRatio={aspectRatio}
        blankImageUrl={blankImageUrl}
        hasShadow={hasShadow}
      />
    );
  }

  return (
    <AppLink
      to={itemLink}
      shouldRefresh={item.shouldRefresh}
      {...linkAnalyticsProps}
      className={cn('block', isCircle && 'flex flex-col items-center')}
      style={outerStyle}
    >
      <div className={tileShellClass}>
        {item.bgColor ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center px-3 text-center"
            style={{ backgroundColor: item.bgColor }}
          >
            <p className="text-sm font-semibold leading-tight text-white md:text-base">{displayTitle}</p>
            {item.collection ? (
              <p className="mt-1 text-xs leading-snug text-white/90 md:text-sm">{item.collection}</p>
            ) : null}
          </div>
        ) : showVideoResolved ? (
          <video
            className="w-full h-full object-cover"
            playsInline
            muted
            loop
            autoPlay
            preload="metadata"
            poster={tileImageSrc}
          >
            <source src={item.videoUrl} type="video/mp4" />
          </video>
        ) : showSliderResolved ? (
          <GridItemSlider
            imageUrls={sliderUrls}
            orientation={resolveSliderOrientation({
              sliderOrientation: item.sliderOrientation,
              aspectRatio,
            })}
            autoPlay={item.sliderAutoPlay ?? true}
            interval={item.sliderInterval ?? 2500}
            title={displayTitle}
            blankImageUrl={blankImageUrl}
            lazyload={item.lazyload}
            isHandpicked={sectionKey === 'handpicked'}
          />
        ) : (
          <img
            src={tileImageSrc}
            alt={displayTitle}
            // object-cover fills container without stretching — crops edges instead if not handpicked
            className={cn("w-full h-full", sectionKey !== 'handpicked' && "object-cover")}
            loading={item.lazyload ? 'lazy' : 'eager'}
            onError={(e) => {
              if (blankImageUrl) {
                (e.target as HTMLImageElement).src = blankImageUrl;
              }
            }}
          />
        )}

        {showBottomOverlay && (
          <div
            className={cn(
              'absolute bottom-0 left-0 right-0 rounded-b-[5px]',
              categoryDesktopMasonry && 'md:rounded-b-2xl',
            )}
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.18) 60%, transparent 100%)',
            }}
          >
            <p
              className={cn(
                'px-3 pb-3 pt-6 font-semibold whitespace-pre-line text-base leading-none tracking-[0.01em] capitalize font-primary md:text-2xl md:pb-4 md:pt-10',
                categoryDesktopMasonry ? 'md:text-left md:pl-4 md:font-serif md:text-xl md:font-normal' : 'text-center',
              )}
              style={{ color: item.overlayTextColor ?? '#ffffff' }}
            >
              {displayTitle}
            </p>
          </div>
        )}
      </div>

      {showTitle && !isOverlay && (
        <p className={cn('mt-1.5 text-center text-[11px] leading-tight', isCircle && 'px-0.5')}>
          {displayTitle}
        </p>
      )}
    </AppLink>
  );
}

/** Minimal typing for Embla internals used for smooth (non-snap) auto-scroll. */
type EmblaEngineForAutoScroll = {
  limit: { length: number };
  scrollBody: { useDuration: (n: number) => unknown };
  scrollTo: { distance: (delta: number, snap: boolean) => void };
};

/**
 * shadcn Carousel (Embla): same tile width as row 1 (`slideWidth`), dragFree for continuous feel,
 * smooth auto-scroll via tiny `scrollTo.distance` steps (not slide-by-slide), stops on user drag / horizontal wheel.
 */
function HandpickedSecondRowEmblaCarousel({
  items,
  slideWidth,
  gap,
  durationSeconds,
  sectionKey,
  shape,
  aspectRatio,
  showTitle,
  titlePosition,
  blankImageUrl,
  overlayBottomDesktopOnly,
  categoryDesktopMasonry,
  autoScrollDelayMs = 0,
  analyticsSectionName,
  handpickedViewportEnabled,
  handpickedViewedPositionsRef,
}: {
  items: SectionItemPresentationVm[];
  slideWidth: string;
  gap: string;
  durationSeconds: number;
  sectionKey: string;
  shape: string;
  aspectRatio: string;
  showTitle: boolean;
  titlePosition: string;
  blankImageUrl: string;
  /** @deprecated forwarded to GridItem but no longer used */
  overlayBottomDesktopOnly: boolean;
  categoryDesktopMasonry: boolean;
  /** Mobile: optional pause before marquee starts (Embla layout); desktop ignores. */
  autoScrollDelayMs?: number;
  analyticsSectionName?: string;
  handpickedViewportEnabled: boolean;
  handpickedViewedPositionsRef: React.MutableRefObject<Set<number>>;
}) {
  const isDesktop = useIsDesktop();

  const [api, setApi] = useState<CarouselApi | null>(null);
  const userStoppedRef = useRef(false);
  const rafRef = useRef(0);

  const stopAuto = useCallback(() => {
    if (userStoppedRef.current) return;
    userStoppedRef.current = true;
    cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    if (!api || userStoppedRef.current) return;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    let last = performance.now();
    let delayTimer: number | undefined;

    const tick = (now: number) => {
      if (userStoppedRef.current) return;
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      const engine = api.internalEngine() as EmblaEngineForAutoScroll;
      const loopLength = engine.limit.length;
      if (loopLength <= 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const pxPerSec = loopLength / durationSeconds;
      engine.scrollBody.useDuration(0);
      engine.scrollTo.distance(-pxPerSec * dt, false);

      rafRef.current = requestAnimationFrame(tick);
    };

    const startRaf = () => {
      last = performance.now();
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };

    const scheduleStart = () => {
      if (delayTimer !== undefined) {
        window.clearTimeout(delayTimer);
        delayTimer = undefined;
      }
      cancelAnimationFrame(rafRef.current);
      if (autoScrollDelayMs > 0) {
        delayTimer = window.setTimeout(() => {
          delayTimer = undefined;
          startRaf();
        }, autoScrollDelayMs);
      } else {
        startRaf();
      }
    };

    scheduleStart();

    const onReInit = () => {
      if (userStoppedRef.current) return;
      scheduleStart();
    };

    api.on('pointerDown', stopAuto);
    api.on('reInit', onReInit);

    return () => {
      if (delayTimer !== undefined) window.clearTimeout(delayTimer);
      cancelAnimationFrame(rafRef.current);
      api.off('pointerDown', stopAuto);
      api.off('reInit', onReInit);
    };
  }, [api, durationSeconds, stopAuto, autoScrollDelayMs]);

  return (
    <div className="w-full px-3 pb-4 pt-2">
      <div style={{ containerType: 'inline-size' } as CSSProperties}>
        <Carousel
          setApi={setApi}
          opts={{
            align: 'start',
            loop: true,
            dragFree: true,
            skipSnaps: true,
            containScroll: 'trimSnaps',
          }}
          className="w-full"
          onWheelCapture={(e) => {
            if (e.deltaX !== 0 || (e.shiftKey && e.deltaY !== 0)) {
              stopAuto();
            }
          }}
        >
          <CarouselContent className="-ml-0 px-2" style={{ gap }} viewportClassName="touch-pan-x">
            {items.map((item) => (
              <CarouselItem
                key={item.position}
                className="min-w-0 shrink-0 pl-0"
                style={{ flex: `0 0 ${slideWidth}` }}
              >
                <HandpickedBannerViewportGate
                  enabled={handpickedViewportEnabled}
                  item={item}
                  isDesktop={isDesktop}
                  viewedPositionsRef={handpickedViewedPositionsRef}
                >
                  <GridItem
                    item={item}
                    sectionKey={sectionKey}
                    shape={shape}
                    aspectRatio={aspectRatio}
                    showTitle={showTitle}
                    titlePosition={titlePosition}
                    blankImageUrl={blankImageUrl}
                    overlayBottomDesktopOnly={overlayBottomDesktopOnly}
                    categoryDesktopMasonry={categoryDesktopMasonry}
                    useGridArea={false}
                    analyticsSectionName={analyticsSectionName}
                  />
                </HandpickedBannerViewportGate>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  );
}

/** Derive column count from the first row of a gridTemplateAreas string */
function getColumnCount(gridTemplateAreas: string): number {
  const firstRow = gridTemplateAreas.match(/'([^']+)'/);
  if (!firstRow) return 1;
  return firstRow[1].trim().split(/\s+/).length;
}

export default function GridSection({ data, sectionKey, eventLabel }: GridSectionProps) {
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const handpickedBannerViewedPositionsRef = useRef<Set<number>>(new Set());
  const handpickedViewportEnabled = sectionKey === 'handpicked';
  const gridTemplate = isDesktop ? data.dGridTemplate : data.gridTemplate;
  const colCount = getColumnCount(gridTemplate);

  const isBento = data.itemAspectRatio === 'auto';
  /** Use Tailwind `md:` so SSR matches and mobile layout stays unchanged below `md` */
  const categoryDesktopMasonry = sectionKey === 'categoryGrid' && isBento;
  const bentoGapResolved = isDesktop && data.bentoGapMd ? data.bentoGapMd : data.bentoGap;
  const bentoRowHeightResolved =
    isDesktop && data.bentoRowHeightMd ? data.bentoRowHeightMd : data.bentoRowHeight;
  const bentoPaddingResolved =
    isDesktop && data.bentoPaddingMd ? data.bentoPaddingMd : data.bentoPadding;

  const visibleItems = useMemo(
    () =>
      data.items.filter((item) =>
        isDesktop ? item.showInDesktop !== false : item.showInMobile !== false,
      ),
    [data.items, isDesktop],
  );
  const splitRowsEnabled = data.splitRows?.enabled === true;
  const firstRowCountConfig = isDesktop
    ? data.splitRows?.firstRowCountDesktop
    : data.splitRows?.firstRowCountMobile;
  const firstRowCount =
    typeof firstRowCountConfig === 'number' && Number.isFinite(firstRowCountConfig) && firstRowCountConfig > 0
      ? Math.floor(firstRowCountConfig)
      : colCount;
  const secondRowScrollable = data.splitRows?.secondRowScrollable !== false;
  const splitRowsForSection = splitRowsEnabled && visibleItems.length > firstRowCount;
  const fixedFirstRowItems = splitRowsForSection
    ? visibleItems.slice(0, firstRowCount)
    : visibleItems;
  const secondRowScrollableItems = splitRowsForSection
    ? visibleItems.slice(firstRowCount)
    : [];
  /** Match first-row cell width: use container query units — % inside a max-content flex row resolves wrong and blows up tile size. */
  const secondRowSlideWidth = `calc((100cqw - (${Math.max(colCount - 1, 0)} * ${data.gap})) / ${Math.max(colCount, 1)})`;
  const secondRowUseMarquee = data.splitRows?.secondRowAutoScroll === true;
  const secondRowMarqueeDelayMs = isMobile ? (data.splitRows?.secondRowAutoScrollDelayMs ?? 0) : 0;
  const marqueeDurationSeconds = (() => {
    const fromJson = data.splitRows?.secondRowMarqueeDurationSeconds;
    if (typeof fromJson === 'number' && Number.isFinite(fromJson) && fromJson > 0) {
      return fromJson;
    }
    return 45;
  })();
  return (
    <section
      style={{
        ...(data.bgGradient ? { background: data.bgGradient } : { backgroundColor: data.bgColor }),
        color: data.textColor,
      }}
      className="pb-5"
      data-analytics-section-name={eventLabel}
    >
      {data.title && (
        <SectionTitle title={data.title} variant={sectionKey === 'categoryGrid' ? 'category' : 'default'} />
      )}

      {splitRowsForSection ? (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${colCount}, 1fr)`,
              gap: data.gap,
              padding: '0 0.75rem',
            }}
          >
            {fixedFirstRowItems.map((item) => (
              <HandpickedBannerViewportGate
                key={item.position}
                enabled={handpickedViewportEnabled}
                item={item}
                isDesktop={isDesktop}
                viewedPositionsRef={handpickedBannerViewedPositionsRef}
              >
                <GridItem
                  item={item}
                  sectionKey={sectionKey}
                  shape={data.itemShape}
                  aspectRatio={data.itemAspectRatio}
                  showTitle={data.showItemTitle}
                  titlePosition={data.itemTitlePosition}
                  blankImageUrl={data.blankImageUrl}
                  overlayBottomDesktopOnly={data.overlayBottomDesktopOnly}
                  categoryDesktopMasonry={categoryDesktopMasonry}
                  useGridArea={false}
                  analyticsSectionName={eventLabel}
                />
              </HandpickedBannerViewportGate>
            ))}
          </div>
          {secondRowScrollableItems.length > 0 && secondRowScrollable && secondRowUseMarquee && (
            <HandpickedSecondRowEmblaCarousel
              key={secondRowScrollableItems.map((i) => i.position).join('-')}
              items={secondRowScrollableItems}
              slideWidth={secondRowSlideWidth}
              gap={data.gap}
              durationSeconds={marqueeDurationSeconds}
              sectionKey={sectionKey}
              shape={data.itemShape}
              aspectRatio={data.itemAspectRatio}
              showTitle={data.showItemTitle}
              titlePosition={data.itemTitlePosition}
              blankImageUrl={data.blankImageUrl}
              overlayBottomDesktopOnly={data.overlayBottomDesktopOnly}
              categoryDesktopMasonry={categoryDesktopMasonry}
              autoScrollDelayMs={secondRowMarqueeDelayMs}
              analyticsSectionName={eventLabel}
              handpickedViewportEnabled={handpickedViewportEnabled}
              handpickedViewedPositionsRef={handpickedBannerViewedPositionsRef}
            />
          )}
          {secondRowScrollableItems.length > 0 && secondRowScrollable && !secondRowUseMarquee && (
            <div className="w-full px-3 pb-4 pt-2">
              <div
                className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                style={{ containerType: 'inline-size' } as CSSProperties}
              >
              <div className="flex w-max snap-none" style={{ gap: data.gap }}>
                {secondRowScrollableItems.map((item) => (
                  <div key={item.position} className="min-w-0 shrink-0" style={{ width: secondRowSlideWidth }}>
                    <HandpickedBannerViewportGate
                      enabled={handpickedViewportEnabled}
                      item={item}
                      isDesktop={isDesktop}
                      viewedPositionsRef={handpickedBannerViewedPositionsRef}
                    >
                      <GridItem
                        item={item}
                        sectionKey={sectionKey}
                        shape={data.itemShape}
                        aspectRatio={data.itemAspectRatio}
                        showTitle={data.showItemTitle}
                        titlePosition={data.itemTitlePosition}
                        blankImageUrl={data.blankImageUrl}
                        overlayBottomDesktopOnly={data.overlayBottomDesktopOnly}
                        categoryDesktopMasonry={categoryDesktopMasonry}
                        useGridArea={false}
                        analyticsSectionName={eventLabel}
                      />
                    </HandpickedBannerViewportGate>
                  </div>
                ))}
              </div>
              </div>
            </div>
          )}
          {secondRowScrollableItems.length > 0 && !secondRowScrollable && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${colCount}, 1fr)`,
                gap: data.gap,
                padding: '0.5rem 0.75rem 1rem',
              }}
            >
              {secondRowScrollableItems.map((item) => (
                <HandpickedBannerViewportGate
                  key={item.position}
                  enabled={handpickedViewportEnabled}
                  item={item}
                  isDesktop={isDesktop}
                  viewedPositionsRef={handpickedBannerViewedPositionsRef}
                >
                  <GridItem
                    item={item}
                    sectionKey={sectionKey}
                    shape={data.itemShape}
                    aspectRatio={data.itemAspectRatio}
                    showTitle={data.showItemTitle}
                    titlePosition={data.itemTitlePosition}
                    blankImageUrl={data.blankImageUrl}
                    overlayBottomDesktopOnly={data.overlayBottomDesktopOnly}
                    categoryDesktopMasonry={categoryDesktopMasonry}
                    useGridArea={false}
                    analyticsSectionName={eventLabel}
                  />
                </HandpickedBannerViewportGate>
              ))}
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateAreas: gridTemplate,
            gridTemplateColumns: `repeat(${colCount}, 1fr)`,
            ...(isBento
              ? {
                  gridAutoRows: bentoRowHeightResolved,
                  gap: bentoGapResolved,
                  padding: bentoPaddingResolved,
                }
              : { gap: data.gap, padding: '0 0.75rem 1rem' }),
          }}
        >
          {visibleItems.map((item) => (
            <HandpickedBannerViewportGate
              key={item.position}
              enabled={handpickedViewportEnabled}
              item={item}
              isDesktop={isDesktop}
              viewedPositionsRef={handpickedBannerViewedPositionsRef}
            >
              <GridItem
                item={item}
                sectionKey={sectionKey}
                shape={data.itemShape}
                aspectRatio={data.itemAspectRatio}
                showTitle={data.showItemTitle}
                titlePosition={data.itemTitlePosition}
                blankImageUrl={data.blankImageUrl}
                overlayBottomDesktopOnly={data.overlayBottomDesktopOnly}
                categoryDesktopMasonry={categoryDesktopMasonry}
                analyticsSectionName={eventLabel}
              />
            </HandpickedBannerViewportGate>
          ))}
        </div>
      )}
    </section>
  );
}
